export type QueryValue = string | number | boolean | null | undefined;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly details: unknown;

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const baseUrl =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "/api";

function csrfToken(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const token = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find(
      (part) =>
        part.startsWith("fbucks_csrf=") ||
        part.startsWith("XSRF-TOKEN=") ||
        part.startsWith("fb_csrf="),
    );
  return token ? decodeURIComponent(token.slice(token.indexOf("=") + 1)) : undefined;
}

function errorMessage(payload: unknown, fallback: string): { message: string; code?: string } {
  if (!payload || typeof payload !== "object") return { message: fallback };
  const record = payload as Record<string, unknown>;
  const rawMessage = record.message ?? record.error ?? record.title;
  const message = Array.isArray(rawMessage)
    ? rawMessage.filter((value): value is string => typeof value === "string").join(" · ")
    : typeof rawMessage === "string"
      ? rawMessage
      : fallback;
  return typeof record.code === "string" ? { message, code: record.code } : { message };
}

export function withQuery(path: string, values: Record<string, QueryValue>): string {
  const query = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") query.set(key, String(value));
  });
  const encoded = query.toString();
  return encoded ? `${path}?${encoded}` : path;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  retryExpiredCsrf = true,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  const bodyIsForm = init.body instanceof FormData;
  if (init.body && !bodyIsForm && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const token = csrfToken();
  if (token) headers.set("X-CSRF-Token", token);
  if (
    init.method &&
    init.method !== "GET" &&
    init.method !== "HEAD" &&
    !headers.has("Idempotency-Key")
  ) {
    headers.set("Idempotency-Key", crypto.randomUUID());
  }

  let response: Response;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      ...init,
      credentials: "include",
      headers,
    });
  } catch {
    throw new ApiError(
      "Der Server ist gerade nicht erreichbar. Prüfe deine Verbindung und versuche es erneut.",
      0,
      "NETWORK_ERROR",
    );
  }

  const contentType = response.headers.get("content-type") ?? "";
  const payload: unknown =
    response.status === 204
      ? undefined
      : contentType.includes("application/json")
        ? await response.json().catch(() => undefined)
        : await response.text().catch(() => undefined);

  if (!response.ok) {
    const fallback =
      response.status === 401
        ? "Deine Sitzung ist abgelaufen."
        : response.status === 403
          ? "Für diese Aktion fehlt dir die Berechtigung."
          : `Die Anfrage ist fehlgeschlagen (${response.status}).`;
    const parsed = errorMessage(payload, fallback);
    const mutating = init.method !== undefined && init.method !== "GET" && init.method !== "HEAD";
    if (retryExpiredCsrf && mutating && response.status === 403 && parsed.code === "CSRF_INVALID") {
      await request<unknown>("/auth/csrf", { method: "GET" }, false);
      return request<T>(path, { ...init, headers }, false);
    }
    throw new ApiError(parsed.message, response.status, parsed.code, payload);
  }
  return payload as T;
}

function jsonBody(body: unknown): BodyInit | undefined {
  return body === undefined ? undefined : JSON.stringify(body);
}

function mutationInit(method: string, body: unknown, init?: RequestInit): RequestInit {
  const encoded = jsonBody(body);
  return {
    ...init,
    method,
    ...(encoded !== undefined ? { body: encoded } : {}),
  };
}

export const api = {
  baseUrl,
  get: <T>(path: string, init?: RequestInit) => request<T>(path, { ...init, method: "GET" }),
  post: <T>(path: string, body?: unknown, init?: RequestInit) =>
    request<T>(path, mutationInit("POST", body, init)),
  put: <T>(path: string, body?: unknown, init?: RequestInit) =>
    request<T>(path, mutationInit("PUT", body, init)),
  patch: <T>(path: string, body?: unknown, init?: RequestInit) =>
    request<T>(path, mutationInit("PATCH", body, init)),
  delete: <T>(path: string, body?: unknown, init?: RequestInit) =>
    request<T>(path, mutationInit("DELETE", body, init)),
  upload: <T>(path: string, formData: FormData, init?: RequestInit) =>
    request<T>(path, { ...init, method: "POST", body: formData }),
};

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function apiErrorText(error: unknown): string {
  return error instanceof Error ? error.message : "Ein unerwarteter Fehler ist aufgetreten.";
}

export const endpoints = {
  csrf: "/auth/csrf",
  me: "/auth/me",
  login: "/auth/login",
  logout: "/auth/logout",
  register: "/auth/register",
  dashboard: "/dashboard",
  notifications: "/notifications",
  activeGames: "/games/active",
  games: "/games",
  tables: "/tables",
  leaderboard: "/leaderboard",
  marketplace: "/marketplace",
  votes: "/votes",
  publicLog: "/logs/public",
  account: "/account",
  sessions: "/account/sessions",
  export: "/account/export",
  team: "/team",
  admin: "/admin/dashboard",
  serverStatus: "/admin/server-status",
  tournaments: "/tournaments",
  flairs: "/flairs",
  androidRelease: "/releases/android/latest",
} as const;
